/**
 * Example Models Dialog Component
 *
 * Browser for example IDF models with category filtering and search.
 */

import { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  useFileStore,
  EXAMPLE_MODELS,
  getComplexityLabel,
} from '../index';
import type { ExampleModel, ExampleCategory } from '../types';

interface ExampleModelsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type CategoryFilter = 'all' | ExampleCategory;

export function ExampleModelsDialog({ open, onOpenChange }: ExampleModelsDialogProps) {
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { loadExampleModel, isLoading } = useFileStore();

  const filteredModels = useMemo(() => {
    let models = EXAMPLE_MODELS;

    // Filter by category
    if (category !== 'all') {
      models = models.filter((m) => m.category === category);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      models = models.filter(
        (m) =>
          m.name.toLowerCase().includes(query) ||
          m.description.toLowerCase().includes(query) ||
          m.features.some((f) => f.toLowerCase().includes(query))
      );
    }

    return models;
  }, [category, searchQuery]);

  const handleOpenModel = useCallback(
    async (model: ExampleModel) => {
      try {
        setError(null);
        await loadExampleModel(model.idfUrl, model.name);
        onOpenChange(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load example model');
      }
    },
    [loadExampleModel, onOpenChange]
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (!open) return null;

  const categories: { value: CategoryFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'tutorials', label: 'Tutorials' },
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'hvac-examples', label: 'HVAC Examples' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex h-[80vh] w-full max-w-4xl flex-col rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Example Models</h2>
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); }}
                className="w-48 rounded-md border bg-background py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              onClick={handleClose}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 border-b px-6 py-2">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => { setCategory(cat.value); }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                category === cat.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 flex items-center gap-2 rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
            <button
              onClick={() => { setError(null); }}
              className="ml-auto hover:text-destructive/80"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Model grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mb-4 text-muted-foreground"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <p className="text-muted-foreground">No models found</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search or filter
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredModels.map((model) => (
                <ExampleModelCard
                  key={model.id}
                  model={model}
                  onOpen={handleOpenModel}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ExampleModelCardProps {
  model: ExampleModel;
  onOpen: (model: ExampleModel) => void;
}

function ExampleModelCard({ model, onOpen }: ExampleModelCardProps) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-md">
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted">
        <ModelThumbnail model={model} />
        <div className="absolute right-2 top-2">
          <ComplexityBadge complexity={model.complexity} />
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-semibold">{model.name}</h3>
        <p className="mt-1 flex-1 text-sm text-muted-foreground line-clamp-2">
          {model.description}
        </p>

        {/* Features */}
        <div className="mt-3 flex flex-wrap gap-1">
          {model.features.slice(0, 3).map((feature) => (
            <span
              key={feature}
              className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              {feature}
            </span>
          ))}
          {model.features.length > 3 && (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              +{model.features.length - 3}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4">
          <Button
            size="sm"
            className="w-full"
            onClick={() => { onOpen(model); }}
          >
            Open
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModelThumbnail({ model }: { model: ExampleModel }) {
  // Generate a simple placeholder based on category
  const getCategoryIcon = () => {
    switch (model.category) {
      case 'residential':
        return (
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        );
      case 'commercial':
        return (
          <>
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <path d="M9 22v-4h6v4" />
            <path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01" />
          </>
        );
      case 'hvac-examples':
        return (
          <>
            <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z" />
          </>
        );
      case 'tutorials':
      default:
        return (
          <>
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </>
        );
    }
  };

  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/10">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-muted-foreground/50"
      >
        {getCategoryIcon()}
      </svg>
    </div>
  );
}

function ComplexityBadge({ complexity }: { complexity: ExampleModel['complexity'] }) {
  const getColors = () => {
    switch (complexity) {
      case 'simple':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'complex':
        return 'bg-red-100 text-red-700 border-red-200';
    }
  };

  const getStars = () => {
    switch (complexity) {
      case 'simple':
        return 1;
      case 'medium':
        return 2;
      case 'complex':
        return 3;
    }
  };

  return (
    <div
      className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${getColors()}`}
    >
      {Array.from({ length: getStars() }).map((_, i) => (
        <svg
          key={i}
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
      <span className="ml-0.5">{getComplexityLabel(complexity)}</span>
    </div>
  );
}
